import fuzzysort from 'fuzzysort';
import {Type} from 'io-ts';
import {isUndefined, uniqBy} from 'lodash';
import React, {useCallback, useEffect, useMemo, useState} from 'react';

import {addQueryToPath} from '../../../../../../libs/shared/helpers/src/browser/urlHelpers';
import {
  useBuildCancelToken,
  useDebounce,
} from '../../../../../../libs/shared/helpers/src/react/hooks/hookHelpers';
import {UnreachableError} from '../../../../../../libs/shared/helpers/src/types/customErrors';
import {
  CancelError,
  CancelToken,
} from '../../../../../../libs/shared/helpers/src/utilities/asyncCancelHelpers';
import {createSelector} from '../../../../../../srcCommon/helpers/reselectHelpers';
import {ModifiersEnum} from '../../../../../../srcCommon/inboxes/search/searchModifiersHelpers';
import {
  isSearchModifierToken,
  isWildcardToken,
  SearchModifierToken,
  SearchToken,
} from '../../../../../../srcCommon/inboxes/search/searchQueryHelpers';
import {
  fuzzySortBaseOptions,
  getPropertiesSuggestions,
  shouldInsertWhitespace,
} from '../../../../../../srcCommon/inboxes/search/searchSuggestionsHelpers';
import {buildTeammateDisplayName} from '../../../../../helpers/modelHelpers';
import {buildTagSearchUrl, SearchTagStatusesEnum} from '../../../../../helpers/models/tagHelpers';
import {useAppSelector} from '../../../../../helpers/reduxHelpers';
import {AccountModel} from '../../../../../models/api/accountModel';
import {ApiType} from '../../../../../models/api/apiTypes';
import {InboxModel} from '../../../../../models/api/inboxModel';
import {
  ApiRecipientContactMatches,
  RecipientContactMatchModel,
} from '../../../../../models/api/recipientMatchModel';
import {ApiSearchAccountsSuggestions} from '../../../../../models/api/searchAccountsV2Model';
import {ApiSearchTagsSuggestions, SearchTagsSuggestion} from '../../../../../models/api/searchTagModel';
import {
  ApiSmartMixMatches,
  SmartMixClassnamesEnum,
  SmartMixMatchModel,
  SmartMixTagMatchModel,
} from '../../../../../models/api/smartMixMatchModel';
import {TagBehaviorsEnum} from '../../../../../models/api/tagModel';
import {TeammateModel} from '../../../../../models/api/teammateModel';
import {fetchApiAsync} from '../../../../../services/http/http';
import {buildRecipientContactMatchSearchUrl} from '../../../../../services/recipientMatches';
import {buildSearchAccountsV2Url} from '../../../../../store/data/accounts/accountsActions';
import {getCompany} from '../../../../../store/data/company/companySelectors';
import {
  getUserOwnedInboxes,
  getUserTeamParticipatingInboxes,
} from '../../../../../store/data/inboxes/inboxesSelectors';
import {getFlatActiveTeammates} from '../../../../../store/data/teammates/teammatesSelectors';
import {
  getCellsSearchQueryHash,
  getCellsSearchRecentQueries,
} from '../../../../../store/panels/cells/search/cellsPanelSearchSelectors';
import {AccountAvatar} from '../../../../common/avatars/accountAvatar';
import {getTagAliasDuplicates} from '../../../composer/toolbar/dropdowns/composerTagsDropdown';
import {
  InboxesSearchSuggestionIconProps,
  InboxesSearchSuggestionItemProps,
  InboxesSuggestionsDropdownItemRecipientAvatar,
  InboxesSuggestionsDropdownItemTeammateAvatar,
} from './inboxesSearchSuggestionsDropdownCommons';
import {SearchSuggestionsDropdownBusinessObjectItemIcon} from './searchSuggestionsDropdownBusinessObjectItemIcon';
import {SearchSuggestionsDropdownTagItemIcon} from './searchSuggestionsDropdownTagItemIcon';
import {SearchSuggestionsDropdownTagItemLabel} from './searchSuggestionsDropdownTagItemLabel';

/**
 * The search dropdown is composed of multiple sections:
 *   - recent queries (aka recent searches), i.e. the queries that the user made previously,
 *   - modifier helpers (aka advanced filters), only displayed if the current token is a wildcard token,
 *   - suggestions, see below,
 *   - conversations suggestions (aka top results).
 *
 * Suggestions are based on the active token, i.e. the token from the query currently under the cursor.
 *
 * We do have 2 types of suggestions, that are never displayed at the same time:
 *   - modifier suggestions, i.e. suggestions related to a modifier.
 *   - smart mix, i.e. a mix of different resources.
 *
 * Modifier suggestions are available for these modifiers:
 *   - inboxes
 *   - author, assignee, participant, mention, commenter (for teammates)
 *   - tags
 *   - recipient, to, from, cc, bcc (for contacts)
 *   - account
 *   - type
 *   - has
 *   - is
 *   - in
 *   - before, after, during
 */

/*
 * Constants.
 */

const MAX_DISPLAYED_RECENT_QUERIES = 3;
const MAX_DISPLAYED_SUGGESTIONS = 5;
// As we might remove suggestions that are already recent queries, fetch enough suggestions.
const MAX_FETCH_SUGGESTIONS = MAX_DISPLAYED_RECENT_QUERIES + MAX_DISPLAYED_SUGGESTIONS;
const MAX_SMART_MIX_SUGGESTIONS = 4;

// @TODO: Remove export once we fetch top results here.
export const SEARCH_DROPDOWN_DEBOUNCE_WAIT_MS = 250;

const inboxSuggestionItemOptions: SuggestionItemOptions<InboxModel> = {
  valueGetter: (inbox) => inbox.alias,
  renderLabel: (inbox) => inbox.name,
  iconName: 'inboxSmall',
};

const teammateSuggestionItemOptions: SuggestionItemOptions<TeammateModel & {displayName: string}> = {
  valueGetter: (teammate) => teammate.alias,
  renderLabel: (teammate) => teammate.displayName,
  renderIcon: (teammate) => <InboxesSuggestionsDropdownItemTeammateAvatar teammate={teammate} />,
};

const tagSuggestionItemOptions: SuggestionItemOptions<SearchTagsSuggestion> = {
  keyGetter: ({tag}) => `${tag.namespace}-${tag.alias}`,
  valueGetter: ({tag}) => tag.alias,
  renderLabel: ({tag, tagsPath}, allItems) => (
    <SearchSuggestionsDropdownTagItemLabel
      tag={tag}
      tagsPath={tagsPath}
      shouldShowDeduplication={getTagAliasDuplicates(allItems.map((item) => item.tagsPath)).has(tag.alias)}
    />
  ),
  renderIcon: ({tag}) => <SearchSuggestionsDropdownTagItemIcon tag={tag} />,
};

const recipientContactSuggestionItemOptions: SuggestionItemOptions<RecipientContactMatchModel> = {
  valueGetter: (recipient) => recipient.handle,
  renderLabel: (recipient) => recipient.name,
  renderIcon: (recipient) => <InboxesSuggestionsDropdownItemRecipientAvatar recipient={recipient} />,
};

const accountSuggestionItemOptions: SuggestionItemOptions<AccountModel> = {
  valueGetter: (account) => String(account.alias),
  renderLabel: (account) => account.name,
  renderIcon: (account) => <AccountAvatar account={account} size={16} avatarIconSize={16} />,
};

/*
 * Types.
 */

type SuggestionItemOptions<T> = {
  keyGetter?: (item: T) => InboxesSearchSuggestionItemProps['key'];
  valueGetter: (item: T) => InboxesSearchSuggestionItemProps['value'];
  renderLabel: (item: T, allItems: ReadonlyArray<T>) => InboxesSearchSuggestionItemProps['label'];
  renderIcon?: (item: T) => InboxesSearchSuggestionItemProps['icon'];
  iconName?: InboxesSearchSuggestionIconProps['iconName'];
  iconColor?: InboxesSearchSuggestionIconProps['iconColor'];
};

interface SearchSuggestions {
  recentQueries: ReadonlyArray<InboxesSearchSuggestionItemProps>;
  suggestions: ReadonlyArray<InboxesSearchSuggestionItemProps>;
}

/*
 * Hook.
 */

export function useSearchSuggestions(
  query: string | undefined,
  activeToken: SearchToken | undefined,
  namespace?: string,
): SearchSuggestions {
  const sanitizedQuery = query ? query.replace(/\s+/g, ' ') : '';

  const queryHash = useAppSelector(getCellsSearchQueryHash);

  // Recent queries.
  const allRecentQueries = useAppSelector(getCellsSearchRecentQueries);

  const filteredRecentQueries = useMemo(
    () =>
      (allRecentQueries || [])
        .filter((recentQuery) => sanitizedQuery.length === 0 || recentQuery.startsWith(sanitizedQuery))
        .slice(0, MAX_DISPLAYED_RECENT_QUERIES),
    [sanitizedQuery, allRecentQueries],
  );

  // Suggestions.
  const company = useAppSelector(getCompany);
  const inboxes = useAppSelector(getUserInboxes);
  const teammates = useAppSelector(getEnhancedTeammates);

  const [suggestions, setSuggestions] = useState<ReadonlyArray<InboxesSearchSuggestionItemProps>>([]);

  const fetchSuggestionsAsync = async (searchToken: SearchToken, cancelToken: CancelToken) => {
    if (isUndefined(searchToken.value) || isWildcardToken(searchToken, query)) {
      return setSuggestions([]);
    }

    const encodedQuery = encodeURIComponent(searchToken.value);
    const basePath = company.url;

    try {
      // If the token is a modifier one, we might want to display modifier suggestions.
      if (isSearchModifierToken(searchToken)) {
        switch (searchToken.modifier) {
          case ModifiersEnum.TAG: {
            const path = buildTagSearchUrl(company, encodedQuery, {
              offset: 0,
              limit: MAX_FETCH_SUGGESTIONS,
              behaviors: [TagBehaviorsEnum.CAN_SUGGEST],
              status: SearchTagStatusesEnum.ALL,
            });

            const items = await fetchAsyncItems(
              searchToken,
              ApiSearchTagsSuggestions,
              path,
              cancelToken,
              tagSuggestionItemOptions,
            );

            return setSuggestions(items);
          }

          case ModifiersEnum.RECIPIENT:
          case ModifiersEnum.TO:
          case ModifiersEnum.FROM:
          case ModifiersEnum.CC:
          case ModifiersEnum.BCC: {
            const path = buildRecipientContactMatchSearchUrl(company, encodedQuery, {
              preferredNamespace: namespace,
              limit: MAX_FETCH_SUGGESTIONS,
            });

            const items = await fetchAsyncItems(
              searchToken,
              ApiRecipientContactMatches,
              path,
              cancelToken,
              recipientContactSuggestionItemOptions,
              (matches) => uniqBy(matches, (item) => item.handle),
            );

            return setSuggestions(items);
          }

          case ModifiersEnum.ACCOUNT: {
            const path = buildSearchAccountsV2Url(company.url, {
              query: encodedQuery,
              limit: MAX_FETCH_SUGGESTIONS,
              suggestions: true,
            });

            const items = await fetchAsyncItems(
              searchToken,
              ApiSearchAccountsSuggestions,
              path,
              cancelToken,
              accountSuggestionItemOptions,
            );

            return setSuggestions(items);
          }

          default:
            break;
        }
      }

      // Otherwise, try to display smart mix suggestions.
      const path = addQueryToPath(`${basePath}/search_suggestions/${encodedQuery}`, {
        namespace,
        query_hash: queryHash,
      });
      const items = await fetchSmartMixItems(searchToken, path, cancelToken, teammates);
      return setSuggestions(items);
    } catch (e) {
      if (!(e instanceof CancelError)) {
        throw e;
      }

      return null;
    }
  };

  const fetchSuggestionsAsyncDebounced = useDebounce(fetchSuggestionsAsync, SEARCH_DROPDOWN_DEBOUNCE_WAIT_MS);

  const updateSuggestionsAsync = useCallback(
    async (searchToken: SearchToken, cancelToken: CancelToken) => {
      // Cancel the debounced call if any.
      fetchSuggestionsAsyncDebounced.cancel();

      if (isUndefined(searchToken.value) || isWildcardToken(searchToken, query)) {
        return setSuggestions([]);
      }

      // If the token is a modifier one, we might want to display modifier suggestions.
      if (isSearchModifierToken(searchToken)) {
        switch (searchToken.modifier) {
          case ModifiersEnum.INBOX:
            return setSuggestions(
              searchStoreItems(searchToken, inboxes, ['name', 'alias'], inboxSuggestionItemOptions),
            );

          case ModifiersEnum.AUTHOR:
          case ModifiersEnum.ASSIGNEE:
          case ModifiersEnum.PARTICIPANT:
          case ModifiersEnum.MENTION:
          case ModifiersEnum.COMMENTER:
            return setSuggestions(
              searchStoreItems(
                searchToken,
                teammates,
                ['displayName', 'alias'],
                teammateSuggestionItemOptions,
              ),
            );

          case ModifiersEnum.TYPE:
          case ModifiersEnum.HAS:
          case ModifiersEnum.IS:
          case ModifiersEnum.IN:
          case ModifiersEnum.BEFORE:
          case ModifiersEnum.AFTER:
          case ModifiersEnum.DURING:
            return setSuggestions(searchStaticItems(searchToken));

          default:
            break;
        }
      }

      return fetchSuggestionsAsyncDebounced(searchToken, cancelToken);
    },
    [fetchSuggestionsAsyncDebounced, inboxes, query, teammates],
  );

  const {buildCancelToken} = useBuildCancelToken();
  useEffect(() => {
    // TODO: If we should cancel pending requests when activeToken becomes undefined, we can useCancelableEffect() instead.
    if (activeToken) {
      const cancelToken = buildCancelToken();
      updateSuggestionsAsync(activeToken, cancelToken);
    }
  }, [activeToken, updateSuggestionsAsync, buildCancelToken]);

  return {
    recentQueries: filteredRecentQueries.map((recentQuery) => ({
      value: recentQuery,
      fuzzyQuery: sanitizedQuery,
      onSelectOptions: {
        insertWhiteSpace: false,
        replaceWholeQuery: true,
        isRecentQuery: true,
      },
    })),
    // Filter out suggestions that are duplicates of recent queries.
    suggestions: suggestions
      .filter(({value}) => !filteredRecentQueries.includes(value))
      .slice(0, MAX_DISPLAYED_SUGGESTIONS),
  };
}

/*
 * Helpers.
 */

const getUserInboxes = createSelector(
  [getUserOwnedInboxes, getUserTeamParticipatingInboxes],
  (userOwnedInboxes, userTeamParticipatingInboxes) => userOwnedInboxes.concat(userTeamParticipatingInboxes),
);

const getEnhancedTeammates = createSelector([getFlatActiveTeammates], (teammates) =>
  teammates.map((t) => ({
    ...t,
    displayName: buildTeammateDisplayName(t),
  })),
);

function searchStoreItems<T>(
  activeToken: SearchModifierToken,
  storeItems: ReadonlyArray<T>,
  searchKeys: ReadonlyArray<keyof T>,
  options: SuggestionItemOptions<T>,
): ReadonlyArray<InboxesSearchSuggestionItemProps> {
  const {keyGetter, valueGetter, renderLabel, renderIcon, iconName, iconColor} = options;

  const onSelectOptions = {
    insertWhiteSpace: true,
    isModifierSuggestion: true,
    modifierSuggestionType: activeToken.modifier,
  };

  // Return default items if value is empty.
  if (isUndefined(activeToken.value) || activeToken.value.length === 0) {
    const slicedItems = storeItems.slice(0, MAX_FETCH_SUGGESTIONS);
    return slicedItems.map((item) => {
      const value = `${activeToken.modifier}:${valueGetter(item)}`;

      return {
        key: keyGetter?.(item),
        icon: renderIcon?.(item),
        iconName,
        iconColor,
        value,
        label: renderLabel(item, slicedItems),
        onSelectOptions,
      };
    });
  }

  // Otherwise, search in store.
  const fuzzyResults = fuzzysort
    .go(activeToken.value, [...storeItems], {
      ...fuzzySortBaseOptions,
      keys: searchKeys.map((key) => String(key)),
    })
    .slice(0, MAX_FETCH_SUGGESTIONS);

  const allItems = fuzzyResults.map((r) => r.obj);

  return fuzzyResults.map((item) => {
    const value = `${activeToken.modifier}:${valueGetter(item.obj)}`;

    return {
      key: keyGetter?.(item.obj),
      icon: renderIcon?.(item.obj),
      iconName,
      iconColor,
      value,
      label: renderLabel(item.obj, allItems),
      onSelectOptions,
    };
  });
}

async function fetchAsyncItems<T>(
  activeToken: SearchModifierToken,
  apiType: ApiType<Type<ReadonlyArray<T>, ReadonlyArray<any>, unknown>>,
  path: string,
  cancelToken: CancelToken,
  options: SuggestionItemOptions<T>,
  transformMatches?: (matches: ReadonlyArray<T>) => ReadonlyArray<T>,
): Promise<ReadonlyArray<InboxesSearchSuggestionItemProps>> {
  const {keyGetter, valueGetter, renderLabel, renderIcon, iconName, iconColor} = options;

  const onSelectOptions = {
    insertWhiteSpace: true,
    isModifierSuggestion: true,
    modifierSuggestionType: activeToken.modifier,
  };

  const matches = await fetchApiAsync(apiType, path, {cancelToken});
  const transformedMatches = transformMatches?.(matches) ?? matches;

  return transformedMatches.map((match) => {
    const value = `${activeToken.modifier}:${valueGetter(match)}`;

    return {
      key: keyGetter?.(match),
      icon: renderIcon?.(match),
      iconName,
      iconColor,
      value,
      label: renderLabel(match, transformedMatches),
      onSelectOptions,
    };
  });
}

function searchStaticItems(
  activeToken: SearchModifierToken,
): ReadonlyArray<InboxesSearchSuggestionItemProps> {
  const suggestions = getPropertiesSuggestions(activeToken.modifier);

  const onSelectOptionsBase = {
    isModifierSuggestion: true,
    modifierSuggestionType: activeToken.modifier,
  };

  if (isUndefined(activeToken.value) || activeToken.value.length === 0) {
    return suggestions.map((suggestion) => ({
      ...suggestion,
      onSelectOptions: {
        ...onSelectOptionsBase,
        insertWhiteSpace: isUndefined(suggestion.valuePlaceholder),
      },
    }));
  }

  return fuzzysort
    .go(activeToken.value, [...suggestions], {
      ...fuzzySortBaseOptions,
      key: 'value',
    })
    .map((item) => ({
      ...item.obj,
      onSelectOptions: {
        ...onSelectOptionsBase,
        insertWhiteSpace: isUndefined(item.obj.valuePlaceholder),
      },
    }));
}

async function fetchSmartMixItems(
  activeToken: SearchToken,
  path: string,
  cancelToken: CancelToken,
  teammates: ReadonlyArray<TeammateModel>,
): Promise<ReadonlyArray<InboxesSearchSuggestionItemProps>> {
  if (isInvalidDotToken(activeToken)) {
    return [];
  }

  const matches = await fetchApiAsync(ApiSmartMixMatches, path, {cancelToken});
  const slicedMatches = matches.slice(0, MAX_SMART_MIX_SUGGESTIONS);

  const tagAliasDuplicates = getTagAliasDuplicates(
    slicedMatches
      .filter((i): i is SmartMixTagMatchModel => i.class === SmartMixClassnamesEnum.TAG)
      .map((i) => i.tagsPath),
  );

  return slicedMatches.map((match) => smartMixItemToSuggestion(match, teammates, tagAliasDuplicates));
}

function isInvalidDotToken(token: SearchToken) {
  // When appended to a URL, the browser interprets those as current directory and previous directory.
  // This can lead to HTTP errors, we should skip them.
  return !isSearchModifierToken(token) && (token.value === '.' || token.value === '..');
}

function smartMixItemToSuggestion(
  item: SmartMixMatchModel,
  teammates: ReadonlyArray<TeammateModel>,
  tagAliasDuplicates: Set<string>,
): InboxesSearchSuggestionItemProps {
  const {primaryLabel, secondaryLabel} = item;
  const suggestionValue = computeSmartMixSuggestionValue(item);

  const onSelectOptions = {
    insertWhiteSpace: shouldInsertWhitespace(suggestionValue),
    isSmartMixItem: true,
    smartMixType: item.class,
  };

  switch (item.class) {
    case SmartMixClassnamesEnum.ACCOUNT:
      return {
        value: suggestionValue,
        label: primaryLabel,
        icon: <AccountAvatar account={item.account} size={16} avatarIconSize={16} />,
        onSelectOptions,
      };

    case SmartMixClassnamesEnum.BUSINESS_OBJECT:
      return {
        value: suggestionValue,
        label: primaryLabel,
        icon: <SearchSuggestionsDropdownBusinessObjectItemIcon type={item.type} />,
        onSelectOptions,
      };

    case SmartMixClassnamesEnum.CONTACT:
      return {
        value: suggestionValue,
        label: primaryLabel,
        icon: <InboxesSuggestionsDropdownItemRecipientAvatar recipient={item.recipient} />,
        onSelectOptions,
      };

    case SmartMixClassnamesEnum.INBOX:
      return {
        value: suggestionValue,
        label: primaryLabel,
        iconName: 'inboxSmall',
        onSelectOptions,
      };

    case SmartMixClassnamesEnum.MODIFIER:
      return {
        value: suggestionValue,
        valuePlaceholder: secondaryLabel,
        onSelectOptions,
      };

    case SmartMixClassnamesEnum.PROPERTY:
      return {
        value: suggestionValue,
        onSelectOptions,
      };

    case SmartMixClassnamesEnum.TAG: {
      return {
        key: `${suggestionValue}-${item.tag.namespace}`,
        label: (
          <SearchSuggestionsDropdownTagItemLabel
            tag={item.tag}
            tagsPath={item.tagsPath}
            shouldShowDeduplication={tagAliasDuplicates.has(item.tag.alias)}
          />
        ),
        icon: <SearchSuggestionsDropdownTagItemIcon tag={item.tag} />,
        value: suggestionValue,
        onSelectOptions,
      };
    }

    case SmartMixClassnamesEnum.TEAMMATE: {
      const teammate = teammates.find(({id}) => id === item.teammateId);

      return {
        value: suggestionValue,
        label: primaryLabel,
        icon: <InboxesSuggestionsDropdownItemTeammateAvatar teammate={teammate} />,
        onSelectOptions,
      };
    }

    default:
      throw new UnreachableError(item);
  }
}

function computeSmartMixSuggestionValue(item: SmartMixMatchModel) {
  const {value} = item;

  // If value already includes a modifier, return early.
  if (value.includes(':')) {
    return value;
  }

  // Otherwise, add it.
  // This happens for account and recipient suggestions.
  switch (item.class) {
    case SmartMixClassnamesEnum.ACCOUNT:
      return `${ModifiersEnum.ACCOUNT}:${value}`;

    case SmartMixClassnamesEnum.CONTACT:
      return `${ModifiersEnum.RECIPIENT}:${value}`;

    default:
      return value;
  }
}
